import { useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, 
  faUsers, 
  faCrown, 
  faStore, 
  faTimes,
  faCheck,
  faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

export default function TemplateDetailModal({ template, onSelect, onClose }) {
  const { user } = useAuth();
  const [isUsing, setIsUsing] = useState(false);

  const handleUseTemplate = async () => {
    if (template.price && template.price > 0) {
      // TODO: Implement payment flow for paid templates
      toast.error('Payment integration coming soon');
      return;
    }

    setIsUsing(true);
    try {
      // Increment usage count
      await updateDoc(doc(db, 'templates', template.id), {
        usageCount: increment(1)
      });

      toast.success('Template selected!');
      if (onSelect) onSelect(template);
    } catch (error) {
      console.error('Error using template:', error);
      toast.error('Failed to use template');
    } finally {
      setIsUsing(false);
    }
  };

  const rating = template.rating || 0;
  const usageCount = template.usageCount || 0;

  const getSourceIcon = () => {
    if (template.vendorId) return faStore;
    if (template.createdBy === 'platform') return faCrown;
    return faUsers;
  };

  const getSourceLabel = () => {
    if (template.vendorId) return 'Vendor Template';
    if (template.createdBy === 'platform') return 'Platform Curated';
    return 'Community Template';
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[120] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <FontAwesomeIcon 
                icon={getSourceIcon()} 
                className={`text-xl ${
                  template.createdBy === 'platform' ? 'text-accent' : 
                  template.vendorId ? 'text-primary' : 'text-text-secondary'
                }`}
              />
              <span className="text-sm text-text-secondary">{getSourceLabel()}</span>
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">{template.name}</h2>
            <div className="flex items-center gap-4">
              {/* Rating */}
              <div className="flex items-center gap-1">
                <FontAwesomeIcon icon={faStar} className="text-yellow-400" />
                <span className="font-semibold text-text-primary">{rating.toFixed(1)}</span>
              </div>
              {/* Usage */}
              <div className="flex items-center gap-1">
                <FontAwesomeIcon icon={faUsers} className="text-text-secondary" />
                <span className="text-sm text-text-secondary">{usageCount} uses</span>
              </div>
              {/* Price */}
              {template.price && template.price > 0 && (
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faDollarSign} className="text-primary" />
                  <span className="font-semibold text-primary">${template.price}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Description */}
          {template.description && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-2">Description</h3>
              <p className="text-text-secondary leading-relaxed">{template.description}</p>
            </div>
          )}

          {/* Template Preview */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-text-primary mb-3">What's Included</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {template.decisionCards && template.decisionCards.length > 0 && (
                <div>
                  <span className="font-semibold text-text-primary">Decision Cards: </span>
                  <span className="text-text-secondary">{template.decisionCards.length} cards</span>
                </div>
              )}
              {template.tasks && template.tasks.length > 0 && (
                <div>
                  <span className="font-semibold text-text-primary">Tasks: </span>
                  <span className="text-text-secondary">{template.tasks.length} tasks</span>
                </div>
              )}
              {template.timeline && template.timeline.length > 0 && (
                <div>
                  <span className="font-semibold text-text-primary">Timeline: </span>
                  <span className="text-text-secondary">{template.timeline.length} milestones</span>
                </div>
              )}
            </div>
          </div>

          {/* Reviews Preview */}
          {template.reviews && template.reviews.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-3">Reviews</h3>
              <div className="space-y-4">
                {template.reviews.slice(0, 3).map((review, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <FontAwesomeIcon
                            key={i}
                            icon={faStar}
                            className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-semibold text-text-primary">
                        {review.userName || 'Anonymous'}
                      </span>
                    </div>
                    <p className="text-text-secondary text-sm">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-white border-2 border-border rounded-lg font-semibold hover:border-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUseTemplate}
            disabled={isUsing}
            className="flex-1 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isUsing ? 'Using Template...' : (
              <>
                <FontAwesomeIcon icon={faCheck} />
                Use This Template
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
